import { useEffect, useState, useCallback } from "react";
import { FlaskConical, Eye, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const MODES = [
  { key: "A", label: "Version A only" },
  { key: "B", label: "Version B only" },
  { key: "split", label: "A/B Split (50/50)" },
];

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

const ROWS = [
  ["assigned", "Assigned"],
  ["mailed", "Mailed"],
  ["unique_scanned", "QR scans (unique)"],
  ["eligibility_starts", "Eligibility starts"],
  ["eligibility_completions", "Eligibility completions"],
  ["appointments_booked", "Appointments booked"],
  ["aware4_99_bookings", "$99 AWARE-4 bookings"],
  ["aware4_99_paid", "Paid conversions"],
  ["revenue", "Revenue", money],
  ["revenue_per_recipient", "Revenue / recipient", money],
  ["scan_rate", "Scan rate", pct],
  ["lead_rate", "Lead rate", pct],
  ["conversion_rate", "Conversion (appts / mailed)", pct],
];

// A/B letter test controls + Version A vs B performance for one campaign.
export default function LetterABPanel({ campaignId, campaign, onChanged }) {
  const [perf, setPerf] = useState(null);
  const [busy, setBusy] = useState(false);
  const mode = campaign?.mail_variant || "A";

  const load = useCallback(() => {
    api.get(`/admin/property-campaigns/${campaignId}/letter-performance`)
      .then(({ data }) => setPerf(data))
      .catch(() => {});
  }, [campaignId]);
  useEffect(load, [load]);

  const setMode = async (mv, confirmed = false) => {
    if (mv === mode) return;
    setBusy(true);
    try {
      await api.patch(`/admin/property-campaigns/${campaignId}`,
        { mail_variant: mv, ...(confirmed ? { confirm_mode_change: true } : {}) });
      toast.success(`Letter mode set to ${MODES.find((m) => m.key === mv)?.label}`);
      onChanged?.(); load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nProceed?`)) {
          setBusy(false);
          return setMode(mv, true);
        }
      } else {
        toast.error(e.response?.data?.detail || "Failed to change letter mode");
      }
    } finally { setBusy(false); }
  };

  const previewB = async () => {
    try {
      const token = localStorage.getItem("scwt_token");
      const r = await fetch(
        `${api.defaults.baseURL}/admin/property-campaigns/${campaignId}/letter-preview?force_version=B`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) {
        let detail = "Version B preview failed";
        try { detail = (await r.json()).detail || detail; } catch { /* non-JSON */ }
        throw new Error(detail);
      }
      window.open(URL.createObjectURL(await r.blob()), "_blank");
    } catch (e) { toast.error(e.message || "Version B preview failed"); }
  };

  const setApproval = async (approved) => {
    const msg = approved
      ? "Approve the Version B letter copy?\n\nOnce approved, Version B letters can be submitted for mailing. Approval is pinned to the exact current wording — any copy change will require re-approval."
      : "Revoke Version B approval? Version B letters will be blocked from mailing again.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post(`/admin/property-campaigns/letter-b-approval`, { approved });
      toast.success(approved ? "Version B copy approved" : "Version B approval revoked");
      load(); onChanged?.();
    } catch { toast.error("Failed to update approval"); }
    finally { setBusy(false); }
  };

  const approval = perf?.version_b_approval;
  const assign = perf?.assignment;
  const v = perf?.versions;

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5 mt-6" data-testid="pc-letter-ab-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-bold text-slate-900 text-sm uppercase tracking-wider">
          <FlaskConical className="w-4 h-4 inline mr-1" /> Letter A/B Test
        </h2>
        <div className="flex gap-1.5" data-testid="pc-ab-mode-selector">
          {MODES.map((m) => (
            <button key={m.key} data-testid={`pc-ab-mode-${m.key}`} disabled={busy}
              onClick={() => setMode(m.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${mode === m.key ? "bg-cyan-600 border-cyan-600 text-white" : "border-slate-200 text-slate-600 hover:border-cyan-300"}`}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {assign && (
        <div className="mt-3 text-xs text-slate-600" data-testid="pc-ab-assignment-counts">
          Version A assigned: <b>{assign.version_a_assigned}</b> · Version B assigned: <b>{assign.version_b_assigned}</b>
          {assign.unassigned > 0 && <> · Not yet assigned: <b>{assign.unassigned}</b> (assigned at letter generation)</>}
          <span className="ml-2 text-slate-400">Assignments are permanent — retries and resends always reuse each recipient&apos;s original version.</span>
        </div>
      )}

      {approval && (
        <div data-testid="pc-ab-approval-banner"
          className={`mt-3 rounded-xl border p-3.5 flex flex-wrap items-center justify-between gap-3 ${approval.approved ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-300"}`}>
          <div className="flex items-start gap-2 text-sm">
            {approval.approved
              ? <ShieldCheck className="w-4 h-4 text-emerald-600 mt-0.5" />
              : <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5" />}
            <div>
              {approval.approved ? (
                <span className="font-bold text-emerald-900">Version B copy approved
                  <span className="font-normal text-emerald-800"> — by {approval.approved_by} · Version B letters can be mailed.</span></span>
              ) : (
                <span className="font-bold text-amber-900">Version B copy: DRAFT — COPY TO BE APPROVED
                  <span className="font-normal text-amber-800"> — previews are watermarked and NO Version B letter can be sent until you review the rendered PDF and approve it.</span></span>
              )}
              {approval.copy_changed_since_approval && (
                <div className="text-xs text-rose-700 font-semibold mt-0.5">The Version B wording changed since approval — re-approval required.</div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="pc-ab-preview-b" variant="outline" className="rounded-full" size="sm" onClick={previewB}>
              <Eye className="w-3.5 h-3.5 mr-1" /> Preview Version B PDF
            </Button>
            {approval.approved ? (
              <Button data-testid="pc-ab-revoke" variant="outline" size="sm" disabled={busy}
                className="rounded-full border-rose-200 text-rose-700" onClick={() => setApproval(false)}>Revoke approval</Button>
            ) : (
              <Button data-testid="pc-ab-approve" size="sm" disabled={busy}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => setApproval(true)}>Approve Version B copy</Button>
            )}
          </div>
        </div>
      )}

      {v && (v.A.assigned > 0 || v.B.assigned > 0) ? (
        <div className="mt-4 overflow-x-auto">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Letter Performance — A vs B</div>
          <table className="w-full text-sm" data-testid="pc-ab-performance-table">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-1.5 pr-3">Metric</th>
                <th className="pr-3">Version A</th>
                <th className="pr-3">Version B</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([key, label, fmt]) => (
                <tr key={key} className="border-t border-slate-100" data-testid={`pc-ab-row-${key}`}>
                  <td className="py-1.5 pr-3 text-slate-600">{label}</td>
                  <td className="pr-3 font-semibold text-slate-900">{fmt ? fmt(v.A[key]) : (v.A[key] ?? "—")}</td>
                  <td className="pr-3 font-semibold text-slate-900">{fmt ? fmt(v.B[key]) : (v.B[key] ?? "—")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : perf ? (
        <div className="mt-4 text-xs text-slate-400" data-testid="pc-ab-no-data">
          Performance comparison appears once recipient letter versions are assigned (at letter generation).
        </div>
      ) : (
        <div className="mt-4 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /></div>
      )}
    </div>
  );
}
